import { Component, OnInit, OnDestroy, ViewChild, ElementRef, Input, Output, EventEmitter } from '@angular/core';
import { FormBuilder, FormGroup, Validators, AbstractControl, FormControl } from "@angular/forms";
import { Router } from "@angular/router";
import { Observable, Subscription, BehaviorSubject, forkJoin, of } from "rxjs";
import { tap, finalize, switchMap, map } from "rxjs/operators";
import { CanDeactivateComponent } from "../../../../common/classes/can-deactivate.component";
import { AuthService } from "../../../../services/auth.service";
import { DynamicDialogService } from "../../../../core/services/dynamic-dialog.service";
import { Dialog, DialogButton } from "../../../../core/models/dialog.model";
import { PermitService } from "../../../../common/services/permit.service";
import {
    LandownerProgramStatusResponse, LandownerProgramStatus, Permit, PermitWaterSource, LandownerProgramApplicationResponse, LandownerPassthroughProgramApplication,
    PassthroughProgramContractor, PassthroughProgramEmergencyData, PassthroughProgramFile, PassthroughProgramLandowner, PassthroughProgramWell, PassthroughProgramInspector, CountyInspector
} from "../../../../common/models/permit.model";
import { DomainDefinitionService } from "../../../../builder/services/domain-definition.service";
import { Well, GISPoint, GISPointTypes } from "../../../../common/models/well.model";
import { IUploadFile } from '../../../../common/services/document-management.service';
import { LoaderService } from "../../../../common/services/loader.service";
import { DocumentManagementService } from "../../../../common/services/document-management.service";
import { OnBaseDocumentTypeProcessReference, OnBaseKeywordMappingSearchSpec, OnBaseMappedKeyword, OnBaseSubProcessName } from "../../../../common/models/onBaseIntegrationAndMapping.model";
import { parseFileNameFromHeaders } from "../../../../common/models/onBaseIntegrationAndMapping.model";

@Component({
    selector: 'submit-passthrough-permit',
    templateUrl: './submit-passthrough-permit.component.html',
    styleUrls: ['./submit-passthrough-permit.component.scss'],
    standalone: false
})
export class SubmitPassthroughPermitComponent extends CanDeactivateComponent implements OnInit, OnDestroy {


    @Input() application: LandownerPassthroughProgramApplication;

    @Input() onBaseDocumentTypes: OnBaseDocumentTypeProcessReference[] = [];

    ineligibleProgramStatuses: LandownerProgramStatus[] = [LandownerProgramStatus.NotFound, LandownerProgramStatus.Active, LandownerProgramStatus.Plugged, LandownerProgramStatus.IneligibleDisposition, LandownerProgramStatus.InProcess, LandownerProgramStatus.AwaitingReview, LandownerProgramStatus.ReturnedForCorrection ];

    form: FormGroup;
    programStatus: LandownerProgramStatusResponse;

    @ViewChild("downloadLink") public downloadLink: ElementRef;

    private countySubject: BehaviorSubject<string[]> = new BehaviorSubject<string[]>([]);
    private townshipSubject: BehaviorSubject<string[]> = new BehaviorSubject<string[]>([]);

    countyObservable: Observable<string[]> = this.countySubject.asObservable();
    townshipObservable: Observable < string[] > = this.townshipSubject.asObservable();

    get countiesLoaded(): boolean {
        return this.countySubject.value.length > 0;
    }

    get townshipsLoaded(): boolean {
        return this.townshipSubject.value.length > 0;
    }

    countyLoader: Observable<boolean> = this.countyObservable.pipe(map((data: string[]) => { return data.length == 0; }));
    townshipLoader: Observable<boolean> = this.townshipObservable.pipe(map((data: string[]) => { return data.length == 0; }));


    get contractorAddressForm(): FormGroup {
        if (this.form == null) return null;
        return this.form.get("contractorAddress") as FormGroup;
    }

    get emergencyDataForm(): FormGroup {
        if (this.form == null) return null;
        return this.form.get("emergencyData") as FormGroup;
    }

    get inspectorForm(): FormGroup {
        if (this.form == null) return null;
        return this.form.get("inspectors") as FormGroup;
    }

    get landownerAddressForm(): FormGroup {
        if (this.form == null) return null;
        return this.form.get("landownerAddress") as FormGroup;
    }

    get programEligibleControl(): AbstractControl {
        if (this.form == null) return null;
        return this.form.get("programEligible");
    }

    get wellDataForm(): FormGroup {
        if (this.form == null) return null;
        return this.form.get("wellData") as FormGroup;
    }


    searchActive: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    states: any[] = [];
    subscription: Subscription = new Subscription();


    currentIncompleteApplicationDialog: Dialog = new Dialog("An application to submit this well to the program has already been started.  Please use that application to continue.", [new DialogButton("OK", true, () => { return true; })]);
    applicationReturnedForCorrectionsDialog: Dialog = new Dialog("An existing landowner passthrough application for that well exists and has been returned for corrections.  Please correct that application and resubmit to continue.", [new DialogButton("OK", true, () => { return true; })]);
    pendingApprovalApplicationDialog: Dialog = new Dialog("An existing landowner passthrough application for that well exists and is currently awaiting review and approval.  Please allow a decision to be made on that application before submitting a new application.", [new DialogButton("OK", true, () => { return true; })]);
    wellActiveInProgramDialog: Dialog = new Dialog("Investigation Needed: This API is already in the Landowner Pass-through program.  Previous application for this API Number must be removed from the Landowner Pass-through program before proceeding with this application.", [new DialogButton("OK", true, () => { return true; })]);
    wellAlreadyPluggedDialog: Dialog = new Dialog("Well has already been plugged as part of the Landowner pass-through payment program.", [new DialogButton("OK", true, () => { return true; })]);
    submitConfirmationDialog: Dialog = new Dialog("Application will be submitted and a permit generated.  Please confirm that all information provided is accurate and all supporting documentation has been uploaded.", [new DialogButton("Confirm", true, () => { this.submit() }), new DialogButton("Cancel", false, () => { return false; })]);
    cancelConfirmationDialog: Dialog = new Dialog("All entered application data will be deleted.  You will have to start again by searching for an API #.  Are you sure you wish to cancel?", [new DialogButton("Yes", true, () => { this.cancel(); }), new DialogButton("No", false, () => { return false; })]);
    applicationSuccessfulDialog: Dialog = new Dialog("", [new DialogButton("OK", true, () => { this.cancel(); })]);
    saveSuccessfulDialog: Dialog = new Dialog("", [new DialogButton("OK", true, () => { this.home(); }), new DialogButton("Continue Working", true, () => { return true; })])
    successText: string = "Applicaton has been successfully submitted and a permit generated.  Your download should begin shortly.  For future reference, the application number is [XXX].";
    savedText: string = "Your application [XXX] has been saved for future completion.";

    showEmergencySection: boolean = false;
    showContractorSection: boolean = false;
    showDocumentationSection: boolean = false;
    showInspectorSection: boolean = false;
    showLandownerSection: boolean = true;
    showWellInformationSection: boolean = false;
    showWellLocationSection: boolean = false;

    watersheds: any[] = [];

    affidavitString: string = "Proposed Contractor's Affidavit of Insurance and Bonding";

    get affidavitCtrl(): AbstractControl {
        let ctrl: AbstractControl = new FormControl();
        const documentsCtrl = this.form.get("documents");
        if (documentsCtrl != null) {
            ctrl = documentsCtrl.get("affidavits") as AbstractControl;
        }
        return ctrl
    }

    get affidavitFiles(): IUploadFile[] {
        let files: IUploadFile[] = [];
        if (this.affidavitCtrl != null) {
            files = <IUploadFile[]>this.affidavitCtrl.value;
        }
        return files;
    }

    get applicationCtrl(): AbstractControl {
        let ctrl: AbstractControl = new FormControl();
        const documentsCtrl = this.form.get("documents");
        if (documentsCtrl != null) {
            ctrl = documentsCtrl.get("applications") as AbstractControl;
        }
        return ctrl
    }

    get applicationFiles(): IUploadFile[] {
        let files: IUploadFile[] = [];
        if (this.applicationCtrl != null) {
            files = <IUploadFile[]>this.applicationCtrl.value;
        }
        return files;
    }

    get contractorCtrl(): AbstractControl {
        let ctrl: AbstractControl = new FormControl();
        const documentsCtrl = this.form.get("documents");
        if (documentsCtrl != null) {
            ctrl = documentsCtrl.get("contractorFiles") as AbstractControl;
        }
        return ctrl
    }

    get contractorFiles(): IUploadFile[] {
        let files: IUploadFile[] = [];
        if (this.contractorCtrl != null) {
            files = <IUploadFile[]>this.contractorCtrl.value;
        }
        return files;
    }

    get mapCtrl(): AbstractControl {
        let ctrl: AbstractControl = new FormControl();
        const documentsCtrl = this.form.get("documents");
        if (documentsCtrl != null) {
            ctrl = documentsCtrl.get("maps") as AbstractControl;
        }
        return ctrl
    }

    get mapFiles(): IUploadFile[] {
        let files: IUploadFile[] = [];
        if (this.mapCtrl != null) {
            files = <IUploadFile[]>this.mapCtrl.value;
        }
        return files;
    }

    get packetsCtrl(): AbstractControl {
        let ctrl: AbstractControl = new FormControl();
        const documentsCtrl = this.form.get("documents");
        if (documentsCtrl != null) {
            ctrl = documentsCtrl.get("packets") as AbstractControl;
        }
        return ctrl
    }

    get packetFiles(): IUploadFile[] {
        let files: IUploadFile[] = [];
        if (this.packetsCtrl != null) { 
            files = <IUploadFile[]>this.packetsCtrl.value;
        }
        return files;
    }

    get plugCtrl(): AbstractControl {
        let ctrl: AbstractControl = new FormControl();
        const documentsCtrl = this.form.get("documents");
        if (documentsCtrl != null) {
            ctrl = documentsCtrl.get("plugPlans") as AbstractControl;
        }
        return ctrl
    }

    get plugFiles(): IUploadFile[] {
        let files: IUploadFile[] = [];
        if (this.plugCtrl != null) {
            files = <IUploadFile[]>this.plugCtrl.value;
        }
        return files;
    }

    get restorationCtrl(): AbstractControl {
        let ctrl: AbstractControl = new FormControl();
        const documentsCtrl = this.form.get("documents");
        if (documentsCtrl != null) {
            ctrl = documentsCtrl.get("restorationPlans") as AbstractControl;
        }
        return ctrl
    }

    get restorationFiles(): IUploadFile[] {
        let files: IUploadFile[] = [];
        if (this.restorationCtrl != null) {
            files = <IUploadFile[]>this.restorationCtrl.value;
        }
        return files;
    }

    @Output() saveActivated: EventEmitter<LandownerPassthroughProgramApplication> = new EventEmitter<LandownerPassthroughProgramApplication>();
    @Output() submitActivated: EventEmitter<LandownerPassthroughProgramApplication> = new EventEmitter<LandownerPassthroughProgramApplication>();


    constructor(dialog: DynamicDialogService, auth: AuthService, private builder: FormBuilder,
        private permitService: PermitService, private domainService: DomainDefinitionService, private loader: LoaderService, private documentService: DocumentManagementService,
        private router: Router) {
        super(dialog, auth);
    }

    ngOnInit() {
        this.confirmDialog = new Dialog("Any unsubmitted changes will be lost upon leaving this page.  Do you wish to continue?", [new DialogButton("Yes", true, () => { this.allowDeactivate.next(true); }), new DialogButton("No", false, () => { return false; })])
        this.allowDeactivate.next(true);
        this.form = this.buildForm();
        let dirty$ = this.form.statusChanges.pipe().subscribe(
            (change: any) => {
                this.allowDeactivate.next(this.form.pristine);
            },
            (error) => {
                console.error(error);
            }
        );
        this.subscription.add(dirty$);

        const stateObs$: Observable<any> = this.domainService.getValues("STATE").pipe(
            tap((data: any) => {
                data.forEach(d => {
                    const obj = { Code: d.Code, Definition: d.Definition };
                    this.states.push(obj);
                })
            })
        )
        this.watersheds.push({ Code: "", Definition: "Not Provided" });
        const watershedObs$: Observable<any> = this.domainService.getValues("WATERSHED").pipe(
            tap((data: any) => {
                data.forEach(d => {
                    const obj = { Code: d.Code, Definition: d.Definition };
                    this.watersheds.push(obj);
                })
            })
        )
        const county$ = this.documentService.findKeywordMappings({ keywordName: "DOGRM - County" } as OnBaseKeywordMappingSearchSpec)
            .pipe(
                map((keys: OnBaseMappedKeyword[]) => {
                    let options: string[] = [];
                    keys.forEach(k => { k.dropdownValues.forEach(v => { options.push(v.value.toLocaleUpperCase()); }); });
                    options = options.filter(this.onlyUnique);
                    return options;
                }),
                tap((counties: string[]) => {
                    counties.unshift("--");
                    this.countySubject.next(counties);
                })
            );

        const township$ = this.documentService.findKeywordMappings({ keywordName: "DOGRM - Township" } as OnBaseKeywordMappingSearchSpec)
            .pipe(
                map((keys: OnBaseMappedKeyword[]) => {
                    let options: string[] = [];
                    keys.forEach(k => { k.dropdownValues.forEach(v => { options.push(v.value.toLocaleUpperCase()); }); });
                    options = options.filter(this.onlyUnique);
                    return options;
                }),
                tap((townships: string[]) => {
                    townships.unshift("--");
                    this.townshipSubject.next(townships);
                })
            );


        const domainSubs$ = forkJoin(stateObs$, watershedObs$, county$, township$).subscribe(() => {  },
            (error) => { console.error(error); });
        this.subscription.add(domainSubs$);
    }

    ngOnDestroy() {
        if (this.subscription != null) this.subscription.unsubscribe();
    }

    affidavitFileAdded(evt: IUploadFile): void {
        const files: IUploadFile[] = this.affidavitFiles;
        const isNew = files.findIndex(l => l.fileName === evt.fileName) === -1;
        if (isNew) {
            evt.documentType = this.onBaseDocumentTypes.find(d => d.subProcessName == OnBaseSubProcessName.InsuranceAndBondingAffidavit)?.onBaseDocumentTypeName;
            files.push(evt);
            this.affidavitCtrl.setValue(files);
        }
    }

    affidavitFileRemoved(evt: IUploadFile): void {
        const files: IUploadFile[] = this.affidavitFiles;
        const fileIndex = files.findIndex(l => l.fileName === evt.fileName);
        if (fileIndex !== -1) {
            files.splice(fileIndex, 1);
            this.affidavitCtrl.setValue(files);
        }
    }

    applicationFileAdded(evt: IUploadFile): void {
        const files: IUploadFile[] = this.applicationFiles;
        const isNew = files.findIndex(l => l.fileName === evt.fileName) === -1;
        if (isNew) {
            evt.documentType = this.onBaseDocumentTypes.find(d => d.subProcessName == OnBaseSubProcessName.PluggingApplication)?.onBaseDocumentTypeName;
            files.push(evt);
            this.applicationCtrl.setValue(files);
        }
    }

    applicationFileRemoved(evt: IUploadFile): void {
        const files: IUploadFile[] = this.applicationFiles;
        const fileIndex = files.findIndex(l => l.fileName === evt.fileName);
        if (fileIndex !== -1) {
            files.splice(fileIndex, 1);
            this.applicationCtrl.setValue(files);
        }
    }

    private buildAddressForm(): FormGroup {
        return this.builder.group({
            street: ["", []],
            suite: ["", []],
            poBox: ["", []],
            city: ["", []],
            state: ["", []],
            zip: ["", []]
        });
    }

    private buildDocumentForm(): FormGroup {
        return this.builder.group({
            packets: [[], []],
            applications: [[], []],
            contractorFiles: [[], []],
            maps: [[], []],
            affidavits: [[], []],
            plugPlans: [[], []],
            restorationPlans: [[], []]
        });
    }

    private buildEmergencyContactForm(): FormGroup {
        return this.builder.group({
            firePhone: ["", []],
            fireExtension: ["", []],
            medicalPhone: ["", []],
            medicalExtension: ["", []],
            ingress: ["", []]
        });
    }

    private buildInspectorForm(): FormGroup {
        return this.builder.group({
            inspectorName: ["", [Validators.required]],
            inspectorPhone: ["", [Validators.required]],
            backupInspectorName: ["", [Validators.required]],
            backupInspectorPhone: ["", [Validators.required]],
            supervisorName: ["", [Validators.required]],
            supervisorPhone: ["", [Validators.required]]
        });
    }


    private buildForm(): FormGroup {
        let eligible = this.programStatus != null && this.ineligibleProgramStatuses.includes(this.programStatus.status) == false;
        return this.builder.group({
            api: ["", [Validators.required]],
            programEligible: [eligible, [Validators.requiredTrue]],
            landownerName: ["", [Validators.required]],
            landownerAddress: this.buildAddressForm(),
            landownerPhone: ["", []],
            landownerEmail: ["", []],
            contractorName: ["", [Validators.required]],
            contractorRepresentative: ["", [Validators.required]],
            contractorEstimate: ["", []],
            contractorAddress: this.buildAddressForm(),
            contractorOfficePhone: ["", []],
            contractorCellPhone: ["", []],
            contractorEmail: ["", []],
            wellData: this.buildWellForm(),
            emergencyData: this.buildEmergencyContactForm(),
            documents: this.buildDocumentForm(),
            inspectors: this.buildInspectorForm()
        });
    }

    private buildWellForm(): FormGroup {
        return this.builder.group({
            latitude: ["", []],
            longitude: ["", []],
            county: ["", []],
            township: ["", []],
            section: ["", []],
            lot: ["", []],
            tract: ["", []],
            urbanized: ["N", []],
            name: ["", []],
            number: ["", []],
            api: [{ value: "", disabled: true }, []],
            lastOwner: ["", []],
            dateOfLastProduction: ["", []],
            amountLastOil: ["", []],
            amountLastGas: ["", []],
            depthPlugged: ["", []],
            waterSource: ["", []],
            watershed: ["", []],
            amountWater: ["", []]
        });
    }

    cancel(): void {
        this.form.reset();
        this.affidavitCtrl.setValue([]);
        this.packetsCtrl.setValue([]);
        this.contractorCtrl.setValue([]);
        this.applicationCtrl.setValue([]);
        this.plugCtrl.setValue([]);
        this.restorationCtrl.setValue([]);
        this.mapCtrl.setValue([]);
        this.programStatus = null;
        this.form.markAsPristine();
        this.allowDeactivate.next(true);
    }

    composeApplication(): LandownerPassthroughProgramApplication {
        let contractor = {
            contractorName: this.form.get("contractorName").value,
            contractorRepresentative: this.form.get("contractorRepresentative").value,
            streetAddress: this.contractorAddressForm.get("street").value,
            suite: this.contractorAddressForm.get("suite").value,
            postOfficeBox: this.contractorAddressForm.get("poBox").value,
            city: this.contractorAddressForm.get("city").value,
            stateCode: this.contractorAddressForm.get("state").value,
            zipCode: this.contractorAddressForm.get("zip").value,
            officePhone: this.form.get("contractorOfficePhone").value,
            cellPhone: this.form.get("contractorCellPhone").value,
            emailAddress: this.form.get("contractorEmail").value
        } as PassthroughProgramContractor;
        let emergency = {
            firePhoneNumber: this.emergencyDataForm.get("firePhone").value,
            fireExtension: this.emergencyDataForm.get("fireExtension").value,
            medicalPhoneNumber: this.emergencyDataForm.get("medicalPhone").value,
            medicalExtension: this.emergencyDataForm.get("medicalExtension").value,
            meansOfIngress: this.emergencyDataForm.get("ingress").value
        } as PassthroughProgramEmergencyData;
        let urbanizedValue = this.wellDataForm.get("urbanized").value;
        let urbanized: boolean = null;
        if (urbanizedValue != '') {
            urbanized = urbanizedValue == 'Y' ? true : false;
        }
        let county = this.wellDataForm.get("county").value;
        let township = this.wellDataForm.get("township").value;
        let well = {
            wellId: this.form.get("api").value,
            latitude: this.wellDataForm.get("latitude").value,
            longitude: this.wellDataForm.get("longitude").value,
            county: county != '--' ? county : "",
            township: township != '--' ? township: "",
            section: this.wellDataForm.get("section").value,
            lot: this.wellDataForm.get("lot").value,
            tract: this.wellDataForm.get("tract").value,
            urbanized: urbanized,
            wellName: this.wellDataForm.get("name").value,
            wellNumber: this.wellDataForm.get("number").value,
            lastOwner: this.wellDataForm.get("lastOwner").value,
            dateOfLastProduction: this.wellDataForm.get("dateOfLastProduction").value,
            amountOfLastOil: this.wellDataForm.get("amountLastOil").value,
            amountOfLastGas: this.wellDataForm.get("amountLastGas").value,
            depthToBePlugged: this.wellDataForm.get("depthPlugged").value,
            waterSource: this.wellDataForm.get("waterSource").value,
            watershed: this.wellDataForm.get("watershed").value,
            amountWater: this.wellDataForm.get("amountWater").value
        } as PassthroughProgramWell;
        let landowner = {
            ownerName: this.form.get("landownerName").value,
            streetAddress: this.landownerAddressForm.get("street").value,
            suite: this.landownerAddressForm.get("suite").value,
            postOfficeBox: this.landownerAddressForm.get("poBox").value,
            city: this.landownerAddressForm.get("city").value,
            stateCode: this.landownerAddressForm.get("state").value,
            zipCode: this.landownerAddressForm.get("zip").value,
            contactPhone: this.form.get("landownerPhone").value,
            emailAddress: this.form.get("landownerEmail").value
        } as PassthroughProgramLandowner;
        let inspectors = {
            inspectorName: this.inspectorForm.get("inspectorName").value,
            inspectorPhone: this.inspectorForm.get("inspectorPhone").value,
            backupInspectorName: this.inspectorForm.get("backupInspectorName").value,
            backupInspectorPhone: this.inspectorForm.get("backupInspectorPhone").value,
            supervisorName: this.inspectorForm.get("supervisorName").value,
            supervisorPhone: this.inspectorForm.get("supervisorPhone").value
        } as PassthroughProgramInspector;

        let files: PassthroughProgramFile[] = [];
        this.affidavitFiles.forEach(f => {
            files.push(this.convertFile(f));
        });
        this.applicationFiles.forEach(f => {
            files.push(this.convertFile(f));
        });
        this.contractorFiles.forEach(f => {
            files.push(this.convertFile(f));
        });
        this.mapFiles.forEach(f => {
            files.push(this.convertFile(f));
        });
        this.packetFiles.forEach(f => {
            files.push(this.convertFile(f));
        });
        this.plugFiles.forEach(f => {
            files.push(this.convertFile(f));
        });
        this.restorationFiles.forEach(f => {
            files.push(this.convertFile(f));
        });
        return {
            wellId: this.form.get("api").value,
            programStatus: LandownerProgramStatus.InProcess,
            lastUpdatedByUserId: this.auth.User.id,
            lastUpdatedByUserName: this.auth.User.displayName,
            contractorEstimate: this.form.get("contractorEstimate").value,
            passthroughProgramContractor: contractor,
            passthroughProgramEmergencyData: emergency,
            passthroughProgramInspector: inspectors,
            passthroughProgramLandowner: landowner,
            passthroughProgramWell: well,
            passthroughProgramFiles: files
        } as LandownerPassthroughProgramApplication;
    }

    confirmCancel(): void {
        this.cancelConfirmationDialog.toggleDialog(true);
        this.dialogService.updateDialog(this.cancelConfirmationDialog);
    }

    confirmSubmit(): void {
        if (this.form.dirty) {
            this.submitConfirmationDialog.toggleDialog(true);
            this.dialogService.updateDialog(this.submitConfirmationDialog);
        }    
    }

    contractorFileAdded(evt: IUploadFile): void {
        const files: IUploadFile[] = this.contractorFiles;
        const isNew = files.findIndex(l => l.fileName === evt.fileName) === -1;
        if (isNew) {
            evt.documentType = this.onBaseDocumentTypes.find(d => d.subProcessName == OnBaseSubProcessName.ScopeOfWork)?.onBaseDocumentTypeName;
            files.push(evt);
            this.contractorCtrl.setValue(files);
        }
    }

    contractorFileRemoved(evt: IUploadFile): void {
        const files: IUploadFile[] = this.contractorFiles;
        const fileIndex = files.findIndex(l => l.fileName === evt.fileName);
        if (fileIndex !== -1) {
            files.splice(fileIndex, 1);
            this.contractorCtrl.setValue(files);
        }
    }

    convertFile(data: IUploadFile): PassthroughProgramFile {
        return {
            fileName: data.fileName,
            programArea: "",
            contentType: data.type,
            documentType: data.documentType,
            documentGroup: (this.onBaseDocumentTypes.length > 0) ? this.onBaseDocumentTypes[0].onBaseDocumentGroupName : "",
            documentUri: data.blobUri
        } as PassthroughProgramFile;
    }

    home(): void {
        this.allowDeactivate.next(true);
        this.router.navigate(['/home']);
    }

    lastProductionDateChanged(evt: Date): void {
        let lastProductionCtrl = this.wellDataForm.get("dateOfLastProduction");
        if (lastProductionCtrl != null) {
            lastProductionCtrl.setValue(evt);
        }
    }

    mapFileAdded(evt: IUploadFile): void {
        const files: IUploadFile[] = this.mapFiles;
        const isNew = files.findIndex(l => l.fileName === evt.fileName) === -1;
        if (isNew) {
            evt.documentType = this.onBaseDocumentTypes.find(d => d.subProcessName == OnBaseSubProcessName.LocationMap)?.onBaseDocumentTypeName;
            files.push(evt);
            this.mapCtrl.setValue(files);
        }
    }

    mapFileRemoved(evt: IUploadFile): void {
        const files: IUploadFile[] = this.mapFiles;
        const fileIndex = files.findIndex(l => l.fileName === evt.fileName);
        if (fileIndex !== -1) {
            files.splice(fileIndex, 1);
            this.mapCtrl.setValue(files);
        }
    }

    onlyUnique(value, index, self) {
        return self.indexOf(value) === index;
    }

    packetFileAdded(evt: IUploadFile): void {
        const files: IUploadFile[] = this.packetFiles;
        const isNew = files.findIndex(l => l.fileName === evt.fileName) === -1;
        if (isNew) {
            evt.documentType = this.onBaseDocumentTypes.find(d => d.subProcessName == OnBaseSubProcessName.ApplicationPacketReceived)?.onBaseDocumentTypeName;
            files.push(evt);
            this.packetsCtrl.setValue(files);
        }
    }

    packetFileRemoved(evt: IUploadFile): void {
        const files: IUploadFile[] = this.packetFiles;
        const fileIndex = files.findIndex(l => l.fileName === evt.fileName);
        if (fileIndex !== -1) {
            files.splice(fileIndex, 1);
            this.packetsCtrl.setValue(files);
        }
    }

    plugFileAdded(evt: IUploadFile): void {
        const files: IUploadFile[] = this.plugFiles;
        const isNew = files.findIndex(l => l.fileName === evt.fileName) === -1;
        if (isNew) {
            evt.documentType = this.onBaseDocumentTypes.find(d => d.subProcessName == OnBaseSubProcessName.PluggingPlan)?.onBaseDocumentTypeName;
            files.push(evt);
            this.plugCtrl.setValue(files);
        }
    }

    plugFileRemoved(evt: IUploadFile): void {
        const files: IUploadFile[] = this.plugFiles;
        const fileIndex = files.findIndex(l => l.fileName === evt.fileName);
        if (fileIndex !== -1) {
            files.splice(fileIndex, 1);
            this.plugCtrl.setValue(files);
        }
    }

    prefillEmergencyInformation(permits: Permit[]): void {
        let hasFirePhone = permits.find(p => p.firePhoneNumber != null);
        let firePhone: string = "";
        let fireExtension: string = "";
        if (hasFirePhone == null) {
            hasFirePhone = permits.find(p => p.officialContacts != null && p.officialContacts.find(o => o.contactType.toLocaleLowerCase().includes('fire')) != null);
            if (hasFirePhone != null) {
                let savedPhone = hasFirePhone.officialContacts.find(o => o.contactType.toLocaleLowerCase().includes('fire')).phone;
                if (savedPhone.toLocaleLowerCase().includes('extension')) {
                    let phoneParts = savedPhone.toLocaleLowerCase().split('extension');
                    firePhone = phoneParts[0].trim();
                    if (phoneParts.length > 1) {
                        fireExtension = phoneParts[1].trim();
                    }
                }
            }
        } else {
            firePhone = hasFirePhone.firePhoneNumber;
        }
        this.emergencyDataForm.get('firePhone').setValue(firePhone);
        this.emergencyDataForm.get('fireExtension').setValue(fireExtension);
        let hasMedical = permits.find(p => p.medicalServicePhoneNumber != null);
        let medicalPhone: string = "";
        let medicalExtension: string = "";
        if (hasMedical == null) {
            hasMedical = permits.find(p => p.officialContacts != null && p.officialContacts.find(o => o.contactType.toLocaleLowerCase().includes('medical')) != null);
            if (hasMedical != null) {
                let savedPhone = hasMedical.officialContacts.find(o => o.contactType.toLocaleLowerCase().includes('medical')).phone;
                if (savedPhone.toLocaleLowerCase().includes('extension')) {
                    let phoneParts = savedPhone.toLocaleLowerCase().split('extension');
                    medicalPhone = phoneParts[0].trim();
                    if (phoneParts.length > 1) {
                        medicalExtension = phoneParts[1].trim();
                    }
                }
            }
        } else {
            medicalPhone = hasMedical.medicalServicePhoneNumber;
        }
        this.emergencyDataForm.get('medicalPhone').setValue(medicalPhone);
        this.emergencyDataForm.get('medicalExtension').setValue(medicalExtension);
    }

    prefillInspectorInformation(inspectorList: CountyInspector): void {
        this.inspectorForm.get("inspectorName").setValue(inspectorList.inspectorName);
        this.inspectorForm.get("inspectorPhone").setValue(inspectorList.inspectorPhone);
        this.inspectorForm.get("backupInspectorName").setValue(inspectorList.backupInspectorName);
        this.inspectorForm.get("backupInspectorPhone").setValue(inspectorList.backupInspectorPhone);
        this.inspectorForm.get("supervisorName").setValue(inspectorList.supervisorName);
        this.inspectorForm.get("supervisorPhone").setValue(inspectorList.supervisorPhone);
    }

    prefillWellInformation(well: Well, permits: Permit[]): void {
        this.wellDataForm.get("api").setValue(well.id);
        if (well.location != null) {
            let surface: GISPoint = well.location.gisPoints.find(g => g.pointType == GISPointTypes.WellHead);
            if (surface != null) {
                if (surface.county != null && surface.county.name != null) {
                    this.wellDataForm.get("county").setValue(surface.county.name.toLocaleUpperCase());
                }
                if (surface.township != null && surface.township.name != null) {
                    this.wellDataForm.get("township").setValue(surface.township.name.toLocaleUpperCase());
                }
                if (surface.coordinates != null) {
                    let latitude = surface.coordinates.latitude83 != null
                        ? surface.coordinates.latitude83
                        : surface.coordinates.latitude;
                    this.wellDataForm.get("latitude").setValue(latitude);
                    let longitude = surface.coordinates.longitude83 != null
                        ? surface.coordinates.longitude83
                        : surface.coordinates.longitude;
                    this.wellDataForm.get("longitude").setValue(longitude);
                }
                if (surface.section != null) {
                    this.wellDataForm.get("section").setValue(surface.section);
                }
                if (surface.lot != null) {
                    this.wellDataForm.get("lot").setValue(surface.lot);
                }
                if (surface.tract != null) {
                    this.wellDataForm.get("tract").setValue(surface.tract);
                }
            }
            let urbanized = well.location.isUrban != null
                ? well.location.isUrban : null;
            this.wellDataForm.get("urbanized").setValue(urbanized != null ? urbanized ? "Y" : "N" : '');

            let wellName = (well.lease != null && well.lease.name != null)
                ? well.lease.name
                : well.name;
            
            if (wellName != null && wellName.length > 0) {
                this.wellDataForm.get("name").setValue(wellName);
            }
            if (well.wellNumber != null && well.wellNumber.length > 0) {
                this.wellDataForm.get("number").setValue(well.wellNumber);
            }
            if (permits != null && permits.length > 0) {
                let permitsWithProposal: Permit[] = permits.filter(p => p.proposal != null);
                let permitsWithIngress: Permit[] = permits.filter(p => p.ingress != null);
                let permitsWithEgress: Permit[] = permits.filter(p => p.egress != null);
                let permitsWithWaterSources: Permit[] = permits.filter(p => p.waterSources != null && p.waterSources.length > 0);
                if (permitsWithProposal.length > 0) {
                    let hasDepth = permitsWithProposal.filter(p => p.proposal.totalDepth != null);
                    if (hasDepth.length > 0) {
                        this.wellDataForm.get("depthPlugged").setValue(hasDepth[0].proposal.totalDepth);
                    }
                }
                let ingress: string = null;
                if (permitsWithIngress.length > 0) {
                    ingress = permitsWithIngress[0].ingress.path;
                } else if (permitsWithEgress.length > 0) {
                    ingress = permitsWithEgress[0].egress.path;
                }
                this.emergencyDataForm.get("ingress").setValue(ingress);

                if (permitsWithWaterSources.length > 0) {
                    let first: PermitWaterSource = permitsWithWaterSources[0].waterSources[0];
                    let value: string;
                    if (first.isLakeErieSource) {
                        let domainOption = this.watersheds.find(f => f.Definition.toLocaleLowerCase().includes("erie"));
                        if (domainOption != null) value = domainOption.Code;
                    } else if (first.isOhioRiverSource) {
                        let domainOption = this.watersheds.find(f => f.Definition.toLocaleLowerCase().includes("ohio"));
                        if (domainOption != null) value = domainOption.Code;
                    } else if (first.watershed != null && first.watershed == 'OUTOFSTATE') {
                        let domainOption = this.watersheds.find(f => f.Definition.toLocaleLowerCase().includes("out"));
                        if (domainOption != null) value = domainOption.Code;
                    }
                    this.wellDataForm.get("watershed").setValue(value);

                    if (first.estimatedTotalVolume != null) {
                        this.wellDataForm.get("amountWater").setValue(first.estimatedTotalVolume);

                    }
                    if (first.name != null) {
                        this.wellDataForm.get("waterSource").setValue(first.name);
 
                    } else if (first.source != null) {
                        this.wellDataForm.get("waterSource").setValue(first.source);
   
                    }
                }
            }
            if (well.depths != null && well.depths.trueVerticalDepth != null) {
                this.wellDataForm.get("depthPlugged").setValue(well.depths.trueVerticalDepth);

            }

        }
    }

    restorationFileAdded(evt: IUploadFile): void {
        const files: IUploadFile[] = this.restorationFiles;
        const isNew = files.findIndex(l => l.fileName === evt.fileName) === -1;
        if (isNew) {
            evt.documentType = this.onBaseDocumentTypes.find(d => d.subProcessName == OnBaseSubProcessName.RestorationAgreement)?.onBaseDocumentTypeName;
            files.push(evt);
            this.restorationCtrl.setValue(files);
        }
    }

    restorationFileRemoved(evt: IUploadFile): void {
        const files: IUploadFile[] = this.restorationFiles;
        const fileIndex = files.findIndex(l => l.fileName === evt.fileName);
        if (fileIndex !== -1) {
            files.splice(fileIndex, 1);
            this.restorationCtrl.setValue(files);
        }
    }

    saveForLater(): void {
        let application: LandownerPassthroughProgramApplication = this.composeApplication();
        this.saveActivated.emit(application);
        this.allowDeactivate.next(true);       
    }

    searchAPI(): void {
        this.searchActive.next(true);
        const api = this.form.get("api").value;
        this.programStatus = null;        
        const status$ = this.permitService.checkLandownerProgramStatus(api)
            .pipe(
                tap((response: LandownerProgramStatusResponse[]) => {
                    if (response.length > 0) {
                        let matching = response.filter(r => r.well != null && r.well.id == api);
                        if (matching.length == 0 && response[0].status == LandownerProgramStatus.NotFound) {
                            this.programEligibleControl.setValue(false);
                            this.programStatus = response[0];
                        } else {
                            let ineligible = matching.filter(m => this.ineligibleProgramStatuses.find(s => s == m.status)).sort((a, b) => {
                                if (a.lastUpdatedDate > b.lastUpdatedDate) return -1;
                                if (a.lastUpdatedDate < b.lastUpdatedDate) return 1;
                                return 0;
                            });
                            if (ineligible.length > 0) {
                                this.programStatus = ineligible[0];
                                this.programEligibleControl.setValue(false);
                            } else {
                                this.programStatus = matching[0];
                                this.programEligibleControl.setValue(true);
                            }
                        }
                    }                   
                })
            ).subscribe(
                (response: LandownerProgramStatusResponse[]) => {
                    this.searchActive.next(false);
                    this.form = this.buildForm();
                    this.form.get("api").setValue(api);
                    let errorDialog: Dialog = null;
                    switch (this.programStatus.status) {
                        case LandownerProgramStatus.Plugged:
                            errorDialog = this.wellAlreadyPluggedDialog;
                            break;
                        case LandownerProgramStatus.Active:
                            errorDialog = this.wellActiveInProgramDialog;
                            break;
                        case LandownerProgramStatus.InProcess:
                            errorDialog = this.currentIncompleteApplicationDialog;
                            break;
                        case LandownerProgramStatus.ReturnedForCorrection:
                            errorDialog = this.applicationReturnedForCorrectionsDialog;
                            break;
                        case LandownerProgramStatus.AwaitingReview:
                            errorDialog = this.pendingApprovalApplicationDialog;
                            break;
                    }
                    if (errorDialog != null) {
                        errorDialog.toggleDialog(true);
                        this.dialogService.updateDialog(errorDialog);
                    }

                    if (this.programStatus.well != null) {
                        this.prefillWellInformation(this.programStatus.well, this.programStatus.relatedPermits);
                        this.prefillEmergencyInformation(this.programStatus.relatedPermits);
                    } else {
                        this.wellDataForm.reset();
                    }
                    if (this.programStatus.inspectorList != null) {
                        this.prefillInspectorInformation(this.programStatus.inspectorList);
                    }
                },
                (error) => {
                    console.error(error);
                    this.dialogService.showError();
                } 
        );
        this.subscription.add(status$);
    }

    submit(): void {
        if (this.form.valid) {
            let application = this.composeApplication();
            this.loader.toggleLoader(true);            
            let saveSubs$ = this.permitService.submitLandownerPassthroughApplication(application).pipe(
                tap((response: LandownerProgramApplicationResponse) => {
                    this.applicationSuccessfulDialog.message = this.successText.replace("[XXX]", response.applicationId);
                    this.applicationSuccessfulDialog.toggleDialog(true);
                    this.dialogService.updateDialog(this.applicationSuccessfulDialog);
                }),
                switchMap((response: LandownerProgramApplicationResponse) => {
                    if (response != null && response.queuedDocumentId != null) {
                        return this.documentService.downloadFromQueue(response.queuedDocumentId);
                    } else {
                        return of(null);
                    }
                }),
                tap((data) => {
                    if (data != null) {
                        const fileName = parseFileNameFromHeaders(data.headers.get("content-disposition"));
                        const blob = new Blob([data.body], { type: data.body.type });
						const blobUri = window.URL.createObjectURL(blob);
						const anchor = <HTMLAnchorElement>this.downloadLink.nativeElement;
						anchor.href = blobUri;
						anchor.download = fileName;
						anchor.click();
                    }
                }),
                finalize(() => { this.loader.toggleLoader(false); })
            ).subscribe(() => {
                this.form.markAsPristine();
                this.allowDeactivate.next(true);
            },
                (error) => {
                    console.error(error);
                    this.dialogService.showError();
                }
            );
            this.subscription.add(saveSubs$);
        }
    }

    submitForApproval(): void {
        const application = this.composeApplication();
        this.submitActivated.emit(application);
    }

    toggleSection(sectionName: string): void {
        switch (sectionName) {
            case "landowner":
                this.showLandownerSection = !this.showLandownerSection
                break;
            case "contractor":
                this.showContractorSection = !this.showContractorSection;
                break;
            case "wellInformation":
                this.showWellInformationSection = !this.showWellInformationSection;
                break;
            case "wellLocation":
                this.showWellLocationSection = !this.showWellLocationSection;
                break;
            case "emergency":
                this.showEmergencySection = !this.showEmergencySection;
                break;
            case "documentation":
                this.showDocumentationSection = !this.showDocumentationSection;
                break;
            case "inspector":
                this.showInspectorSection = !this.showInspectorSection;
                break;
        }            
    }
}
